/**
 * Off-DOM text measurement for the fill-in-the-blank placeholder.
 *
 * Why this exists: ADR-014 §2 replaces the blank with the answer *in place*.
 * If the blank is narrower than that answer the sentence reflows at the moment
 * of reveal — after the ADR-027 ladder has settled and is forbidden to measure
 * again — and the extra line overflows the container the ladder sized, which
 * clips it at both edges (`justifyContent: center`). Sizing the blank from the
 * real glyph metrics of the text that will replace it removes the reflow at its
 * source. See docs/diagnostics/learn-ladder-height-chain-2026-09-01.md §3/§7.
 *
 * Measurement goes through a canvas rather than a hidden DOM node deliberately:
 * the answer must never exist as text inside the sentence — not transparent,
 * not `visibility: hidden`, not detached-then-removed — where selection, copy,
 * devtools, or the accessibility tree could surface it.
 */

let ctx: CanvasRenderingContext2D | null | undefined
let cachedFamily: string | null = null

const widthCache = new Map<string, number>()

function context(): CanvasRenderingContext2D | null {
  if (ctx === undefined) {
    try {
      ctx = document.createElement('canvas').getContext('2d')
    } catch {
      ctx = null
    }
  }
  return ctx
}

/**
 * The resolved value of `--font-content` as a canvas-usable family list.
 * Read once — the token is a `:root` constant and does not vary by theme.
 */
export function contentFontFamily(): string {
  if (cachedFamily === null) {
    let value = ''
    try {
      value = getComputedStyle(document.documentElement)
        .getPropertyValue('--font-content')
        .trim()
    } catch {
      value = ''
    }
    // Do not cache an empty read (stylesheet not applied yet) — try again next call.
    if (!value) return 'Georgia, serif'
    cachedFamily = value
  }
  return cachedFamily
}

/**
 * Width of `text` in CSS pixels at `font` (a CSS `font` shorthand), or null
 * when canvas metrics are unavailable (jsdom without a canvas backend). Callers
 * must have a layout fallback for null.
 */
export function measureTextWidth(text: string, font: string): number | null {
  const key = `${font} ${text}`
  const hit = widthCache.get(key)
  if (hit !== undefined) return hit
  const c = context()
  if (!c) return null
  c.font = font
  const width = c.measureText(text).width
  if (!Number.isFinite(width) || width <= 0) return null
  widthCache.set(key, width)
  return width
}

// ── Webfont-load invalidation ────────────────────────────────────────────────
// Widths measured before `--font-content`'s webfont finishes loading come from
// the fallback face. Anything laid out from those numbers would keep a stale
// width once the real face swaps in, which is the very reflow this module
// exists to prevent. Clear the cache once fonts are ready and let subscribers
// re-render. In practice this resolves during app boot, long before a Learn
// card mounts; the subscription is insurance, not a hot path.

let generation = 0
const listeners = new Set<() => void>()

const fontSet = typeof document !== 'undefined' ? document.fonts : undefined
if (fontSet?.ready) {
  fontSet.ready
    .then(() => {
      widthCache.clear()
      generation += 1
      listeners.forEach((listener) => listener())
    })
    .catch(() => {})
}

export function subscribeFontMetrics(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function fontMetricsGeneration(): number {
  return generation
}
